import { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Lock, Bug } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { useSelection } from '../../context/SelectionContext';
import { useModules } from '../../hooks/useModules';
import { useReports } from '../../hooks/useReports';
import { useCourses } from '../../hooks/useCourses';
import { useChildren } from '../../hooks/useChildren';
import ModuleCard from './ModuleCard';
import ReportCard from './ReportCard';
import GradeCard from './GradeCard';
import AppointmentScheduler from './AppointmentScheduler';
import { SkeletonModuleList } from '../common/Skeleton';
import EmptyState from '../common/EmptyState';
import FadeTransition from '../common/FadeTransition';
import api from '../../utils/api';
import { formatDate } from '../../utils/dateFormatter';

// Dev mode flag - shows all module status variations for testing
const DEV_MODE = import.meta.env.VITE_DEV_MODE === 'true';

// Generate dates relative to today for DevModePreview display
const getRelativeDate = (daysFromToday) => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromToday);
  return date.toISOString().split('T')[0];
};

// Dev mode component to show all module card status variations
function DevModePreview() {
  const { t } = useTranslation();

  return (
    <div className="border-2 border-dashed border-purple-400 dark:border-purple-600 rounded-xl p-4 mb-4">
      <div className="flex items-center gap-2 mb-4">
        <Bug className="w-5 h-5 text-purple-600 dark:text-purple-400" />
        <span className="text-sm font-bold text-purple-700 dark:text-purple-300">
          {t('devMode.title')}
        </span>
      </div>

      {/* Section 1: Attendance Status */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          1. {t('devMode.attendanceStatus')}
        </p>
        <div className="space-y-2">
          <ModuleCard
            module={{ id: 'dev-1', title: 'Class Module 1', date: getRelativeDate(-1), attendance_status: 1, homework_status: null, video_watched: null, is_canceled: 0, is_makeup: false }}
            isToday={false}
          />
          <p className="text-[10px] text-purple-500 dark:text-purple-400 ml-2">↑ {t('status.present')} ({t('devMode.green')})</p>

          <ModuleCard
            module={{ id: 'dev-2', title: 'Class Module 2', date: getRelativeDate(-2), attendance_status: 2, homework_status: null, video_watched: null, is_canceled: 0, is_makeup: false }}
            isToday={false}
          />
          <p className="text-[10px] text-purple-500 dark:text-purple-400 ml-2">↑ {t('status.late')} ({t('devMode.yellow')})</p>

          <ModuleCard
            module={{ id: 'dev-3', title: 'Class Module 3', date: getRelativeDate(-3), attendance_status: 4, homework_status: null, video_watched: null, is_canceled: 0, is_makeup: false }}
            isToday={false}
          />
          <p className="text-[10px] text-purple-500 dark:text-purple-400 ml-2">↑ {t('status.absent')} ({t('devMode.red')})</p>
        </div>
      </div>

      {/* Section 2: Homework Status */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          2. {t('devMode.homeworkStatus')}
        </p>
        <div className="space-y-2">
          <ModuleCard
            module={{ id: 'dev-4', title: 'Class Module 4', date: getRelativeDate(-4), attendance_status: 1, homework_status: 'on-time', video_watched: null, is_canceled: 0, is_makeup: false }}
            isToday={false}
          />
          <p className="text-[10px] text-purple-500 dark:text-purple-400 ml-2">↑ {t('status.hwAssigned')} ({t('devMode.blue')}) + {t('status.hwOnTime')} ({t('devMode.green')})</p>

          <ModuleCard
            module={{ id: 'dev-5', title: 'Class Module 5', date: getRelativeDate(-5), attendance_status: 1, homework_status: 'late', video_watched: null, is_canceled: 0, is_makeup: false }}
            isToday={false}
          />
          <p className="text-[10px] text-purple-500 dark:text-purple-400 ml-2">↑ {t('status.hwAssigned')} ({t('devMode.blue')}) + {t('status.hwLate')} ({t('devMode.yellow')})</p>

          <ModuleCard
            module={{ id: 'dev-6', title: 'Class Module 6', date: getRelativeDate(-6), attendance_status: 1, homework_status: 'no-submission', video_watched: null, is_canceled: 0, is_makeup: false }}
            isToday={false}
          />
          <p className="text-[10px] text-purple-500 dark:text-purple-400 ml-2">↑ {t('status.hwAssigned')} ({t('devMode.blue')}) + {t('status.hwMissing')} ({t('devMode.red')})</p>
        </div>
      </div>

      {/* Section 3: Video Status */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          3. {t('devMode.videoStatus')}
        </p>
        <div className="space-y-2">
          <ModuleCard
            module={{ id: 'dev-7', title: 'Class Module 7', date: getRelativeDate(-7), attendance_status: 1, homework_status: null, video_watched: true, is_canceled: 0, is_makeup: false }}
            isToday={false}
          />
          <p className="text-[10px] text-purple-500 dark:text-purple-400 ml-2">↑ {t('status.watched')} ({t('devMode.green')})</p>

          <ModuleCard
            module={{ id: 'dev-8', title: 'Class Module 8', date: getRelativeDate(-8), attendance_status: 1, homework_status: null, video_watched: false, is_canceled: 0, is_makeup: false }}
            isToday={false}
          />
          <p className="text-[10px] text-purple-500 dark:text-purple-400 ml-2">↑ {t('status.notWatched')} ({t('devMode.red')})</p>
        </div>
      </div>

      {/* Section 4: Module Badges */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          4. {t('devMode.moduleBadges')}
        </p>
        <div className="space-y-2">
          <ModuleCard
            module={{ id: 'dev-9', title: 'Class Module 9', date: getRelativeDate(0), attendance_status: 1, homework_status: 'on-time', video_watched: true, is_canceled: 0, is_makeup: false }}
            isToday={true}
          />
          <p className="text-[10px] text-purple-500 dark:text-purple-400 ml-2">↑ {t('module.today')} ({t('devMode.blue')})</p>

          <ModuleCard
            module={{ id: 'dev-10', title: 'Class Module 10', date: getRelativeDate(-9), attendance_status: 1, homework_status: 'on-time', video_watched: true, is_canceled: 0, is_makeup: true }}
            isToday={false}
          />
          <p className="text-[10px] text-purple-500 dark:text-purple-400 ml-2">↑ {t('module.makeup')} ({t('devMode.yellow')})</p>
        </div>
      </div>

      {/* Section 5: Canceled Variations */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          5. {t('devMode.canceledVariations')}
        </p>
        <div className="space-y-2">
          <ModuleCard
            module={{ id: 'dev-11', title: 'Class Module 11', date: getRelativeDate(-10), attendance_status: null, homework_status: null, video_watched: null, is_canceled: 1, is_makeup: false }}
            isToday={false}
          />
          <p className="text-[10px] text-purple-500 dark:text-purple-400 ml-2">↑ {t('module.canceledByStudent')} ({t('devMode.gray')})</p>

          <ModuleCard
            module={{ id: 'dev-12', title: 'Class Module 12', date: getRelativeDate(-11), attendance_status: null, homework_status: null, video_watched: null, is_canceled: 2, is_makeup: false }}
            isToday={false}
          />
          <p className="text-[10px] text-purple-500 dark:text-purple-400 ml-2">↑ {t('module.canceledByTeacher')} ({t('devMode.gray')})</p>

          <ModuleCard
            module={{ id: 'dev-13', title: 'Class Module 13', date: getRelativeDate(-12), attendance_status: null, homework_status: null, video_watched: null, is_canceled: 3, is_makeup: false }}
            isToday={false}
          />
          <p className="text-[10px] text-purple-500 dark:text-purple-400 ml-2">↑ {t('module.canceledByOffice')} ({t('devMode.gray')})</p>
        </div>
      </div>

      {/* Section 6: Combined Status Example */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          6. {t('devMode.combinedStatus')}
        </p>
        <div className="space-y-2">
          <ModuleCard
            module={{ id: 'dev-14', title: 'Class Module 14', date: getRelativeDate(-13), attendance_status: 2, homework_status: 'late', video_watched: false, is_canceled: 0, is_makeup: true }}
            isToday={false}
          />
          <p className="text-[10px] text-purple-500 dark:text-purple-400 ml-2">
            ↑ {t('module.makeup')} + {t('status.late')} + {t('status.hwAssigned')} + {t('status.hwLate')} + {t('status.notWatched')}
          </p>
        </div>
      </div>

      {/* Section 7: Future Module */}
      <div>
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          7. {t('devMode.futureModule')}
        </p>
        <div className="space-y-2">
          <ModuleCard
            module={{ id: 'dev-15', title: 'Class Module 15', date: getRelativeDate(7), attendance_status: null, homework_status: null, video_watched: null, is_canceled: 0, is_makeup: false }}
            isToday={false}
          />
          <p className="text-[10px] text-purple-500 dark:text-purple-400 ml-2">
            ↑ {t('devMode.futureModuleDesc')}
          </p>
        </div>
      </div>
    </div>
  );
}

// Check if a course is upcoming (hasn't started yet)
const isUpcomingCourse = (course) => {
  if (!course?.start_date) return false;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startDate = new Date(course.start_date);
  const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  return startDateOnly > today;
};

// Format date for display - using locale-aware formatter
const formatStartDate = (dateString) => {
  if (!dateString) return '';
  return formatDate(dateString, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};

export default function ModuleList() {
  const { t } = useTranslation();
  const { selectedChild, selectedCourse } = useSelection();
  const { children, loading: childrenLoading } = useChildren();

  // Only fetch courses if selected child is valid (belongs to this parent)
  const isValidChild = selectedChild && children.some(c => c.id === selectedChild.id);
  const { courses, loading: coursesLoadingRaw } = useCourses(isValidChild ? selectedChild?.id : null);

  // Consider loading if either children or courses are loading
  const coursesLoading = childrenLoading || coursesLoadingRaw;
  const isUpcoming = isUpcomingCourse(selectedCourse);

  // Only fetch modules if course is not upcoming
  const shouldFetchModules = !isUpcoming;
  const { modules, loading, error } = useModules(
    isValidChild ? selectedChild?.id : null,
    shouldFetchModules ? selectedCourse?.id : null
  );

  // Fetch reports and grades for the course
  const { reports, grades, loading: reportsLoading } = useReports(
    isValidChild ? selectedChild?.id : null,
    shouldFetchModules ? selectedCourse?.id : null
  );

  // Use raw loading states - no artificial delay
  const combinedLoading = loading || reportsLoading;

  // Track competition details submission status for R-/O- courses
  // Use fetched course ID to derive loading state (no extra renders needed)
  const [competitionData, setCompetitionData] = useState({ courseId: null, submitted: false });

  // Derive loading state from whether we've fetched for current course
  const competitionStatusLoading = selectedCourse?.is_competition_research &&
    selectedCourse?.needs_parent_input &&
    competitionData.courseId !== selectedCourse?.id;
  const competitionDetailsSubmitted = competitionData.courseId === selectedCourse?.id && competitionData.submitted;

  // Fetch competition details submission status
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const fetchCompetitionStatus = useCallback(async () => {
    if (!selectedChild?.id || !selectedCourse?.id) return;
    if (!selectedCourse?.is_competition_research || !selectedCourse?.needs_parent_input) {
      setCompetitionData({ courseId: selectedCourse.id, submitted: true });
      return;
    }

    try {
      const response = await api.get(`/parent/children/${selectedChild.id}/courses/${selectedCourse.id}/competition-details`);
      setCompetitionData({ courseId: selectedCourse.id, submitted: response.data.details?.is_submitted || false });
    } catch {
      setCompetitionData({ courseId: selectedCourse.id, submitted: false });
    }
  }, [selectedChild?.id, selectedCourse?.id, selectedCourse?.is_competition_research, selectedCourse?.needs_parent_input]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchCompetitionStatus();
  }, [fetchCompetitionStatus]);

  const todayRef = useRef(null);
  const hasScrolled = useRef(false);

  // Find the module closest to today (today or the most recent past module)
  const todayIndex = useMemo(() => {
    if (modules.length === 0) return -1;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Modules are sorted DESC (newest first), so we find the first module
    // that is on or before today
    for (let i = 0; i < modules.length; i++) {
      const moduleDate = new Date(modules[i].date);
      const moduleDateOnly = new Date(
        moduleDate.getFullYear(),
        moduleDate.getMonth(),
        moduleDate.getDate()
      );

      if (moduleDateOnly <= today) {
        return i;
      }
    }

    // If all modules are in the future, scroll to the last one (closest to today)
    return modules.length - 1;
  }, [modules]);

  // Check if a module is "today"
  const isToday = (moduleDate) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const date = new Date(moduleDate);
    const moduleDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return moduleDateOnly.getTime() === today.getTime();
  };

  // Reset scroll flag when child or course changes
  useEffect(() => {
    hasScrolled.current = false;
  }, [selectedChild?.id, selectedCourse?.id]);

  // Auto-scroll to today's module or closest past module
  useEffect(() => {
    if (modules.length > 0 && !hasScrolled.current) {
      // Use requestAnimationFrame for smoother scroll after render
      const timer = setTimeout(() => {
        requestAnimationFrame(() => {
          if (todayRef.current) {
            todayRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
            hasScrolled.current = true;
          }
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [modules, selectedChild?.id, selectedCourse?.id]);

  // Calculate appointment scheduler positions
  // Early scheduler: BEFORE first non-canceled class (so parents see it before their first held class)
  // Late scheduler: Always 3rd from top (index 2), between 2nd-to-last and 3rd-to-last modules
  // Modules are sorted DESC (newest/last class at index 0, oldest/first class at end)
  const schedulerPositions = useMemo(() => {
    if (modules.length < 2) return { earlyIndex: -1, lateIndex: -1 };

    // Find first non-canceled class (from end of array since modules are DESC)
    // We want to place the scheduler BEFORE this module (between it and the previous canceled one)
    let firstNonCanceledIndex = -1;
    for (let i = modules.length - 1; i >= 0; i--) {
      if (!modules[i].is_canceled) {
        firstNonCanceledIndex = i;
        break;
      }
    }

    // Early scheduler: BEFORE first non-canceled class
    // In DESC order, the module BEFORE firstNonCanceledIndex is at index - 1
    // Scheduler renders AFTER the module at earlyIndex, so use index - 1
    const earlyIndex = firstNonCanceledIndex > 0 ? firstNonCanceledIndex - 1 : -1;

    // Late scheduler: Always at index 1 (renders after index 1, making it 3rd from top)
    // This places it between the 2nd-to-last module (index 1) and 3rd-to-last module (index 2)
    // Need at least 3 modules to show the late scheduler
    const lateIndex = modules.length >= 3 ? 1 : -1;

    // Handle edge case: if both would be same position, only show early
    if (earlyIndex !== -1 && lateIndex !== -1 && earlyIndex === lateIndex) {
      return { earlyIndex, lateIndex: -1 };
    }

    return { earlyIndex, lateIndex };
  }, [modules]);

  // Helper: find insertion index for a given date in DESC-sorted modules
  const findInsertIndex = (publishDate, modulesList) => {
    if (!publishDate || modulesList.length === 0) return -1;

    const pubDate = new Date(publishDate);
    const pubDateOnly = new Date(pubDate.getFullYear(), pubDate.getMonth(), pubDate.getDate());

    for (let i = 0; i < modulesList.length; i++) {
      const moduleDate = new Date(modulesList[i].date);
      const moduleDateOnly = new Date(moduleDate.getFullYear(), moduleDate.getMonth(), moduleDate.getDate());

      if (moduleDateOnly.getTime() <= pubDateOnly.getTime()) {
        return i;
      }
    }

    return modulesList.length;
  };

  // Calculate report positions based on first_published_at dates
  const reportPositions = useMemo(() => {
    if (modules.length === 0 || reports.length === 0) {
      return { midtermIndex: -1, finalIndex: -1, midtermReport: null, finalReport: null };
    }

    const midtermReport = reports.find(r => r.report_type === 'midterm');
    const finalReport = reports.find(r => r.report_type === 'final');

    let midtermIndex = -1;
    let finalIndex = -1;

    if (midtermReport) {
      midtermIndex = midtermReport.first_published_at
        ? findInsertIndex(midtermReport.first_published_at, modules)
        : Math.floor(modules.length / 2);
    }

    if (finalReport) {
      finalIndex = finalReport.first_published_at
        ? findInsertIndex(finalReport.first_published_at, modules)
        : 0;
    }

    return { midtermIndex, finalIndex, midtermReport, finalReport };
  }, [modules, reports]);

  // Calculate grade positions independently based on published_at
  const gradePositions = useMemo(() => {
    if (modules.length === 0 || grades.length === 0) return [];

    return grades.map(grade => ({
      ...grade,
      insertIndex: findInsertIndex(grade.published_at, modules),
    }));
  }, [modules, grades]);

  if (!selectedChild) {
    return null;
  }

  // Unified skeleton for ALL loading states - same count prevents re-render flicker
  const loadingSkeleton = (
    <div className="px-4 py-3 sm:px-6 sm:py-4 md:px-12 lg:px-20">
      <SkeletonModuleList count={5} />
    </div>
  );

  const renderContent = () => {
    // Show unified loading skeleton for all loading states
    if (coursesLoading) {
      return loadingSkeleton;
    }

    // Don't render if no course selected
    if (!selectedCourse) {
      // If courses exist but none selected yet, still show loading (waiting for auto-select)
      if (courses.length > 0) {
        return loadingSkeleton;
      }
      return null; // No courses available
    }

    // Show upcoming course message instead of modules
    if (isUpcoming) {
      return (
        <div className="px-4 py-3 sm:px-6 sm:py-4 md:px-12 lg:px-20">
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 sm:p-8 text-center">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <CalendarDays className="w-6 h-6 sm:w-8 sm:h-8 text-[#589DD5]" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-700 dark:text-gray-200 mb-1.5 sm:mb-2">{t('module.courseStartingSoon')}</h3>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              <Trans i18nKey="module.courseStartsOn" values={{ date: formatStartDate(selectedCourse.start_date) }}>
                This course begins on <span className="font-semibold dark:text-white">{'{{date}}'}</span>
              </Trans>
            </p>
            <p className="text-gray-400 dark:text-gray-500 text-xs sm:text-sm mt-2 sm:mt-3">
              {t('module.modulesAvailableSoon')}
            </p>
          </div>
        </div>
      );
    }

    // Show loading while checking competition details status
    if (selectedCourse?.is_competition_research && selectedCourse?.needs_parent_input && competitionStatusLoading) {
      return loadingSkeleton;
    }

    // Show locked message for R-/O- courses without submitted details
    if (selectedCourse?.is_competition_research && selectedCourse?.needs_parent_input && !competitionDetailsSubmitted) {
      return (
        <div className="px-4 py-3 sm:px-6 sm:py-4 md:px-12 lg:px-20">
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 sm:p-8 text-center">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <Lock className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-700 dark:text-gray-200 mb-1.5 sm:mb-2">{t('module.informationRequired')}</h3>
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 mb-3 sm:mb-4">
              {selectedCourse?.code?.startsWith('R-') ? t('module.provideResearchDetails') : t('module.provideCompetitionDetails')}
            </p>
            <Link
              to="/research"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#589DD5] text-white rounded-lg hover:bg-[#4a8bc4] transition-colors text-sm font-medium"
            >
              {t('actions.enterDetails')}
            </Link>
          </div>
        </div>
      );
    }

    // Show loading while fetching modules/reports
    if (combinedLoading) {
      return loadingSkeleton;
    }

    if (error) {
      return (
        <div className="px-4 py-3 sm:px-6 sm:py-4 md:px-12 lg:px-20">
          <div className="bg-red-50 dark:bg-transparent text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500 px-3 py-2 sm:px-4 sm:py-3 rounded-md text-xs sm:text-sm">
            {error}
          </div>
        </div>
      );
    }

    if (modules.length === 0) {
      return (
        <div className="px-4 py-3 sm:px-6 sm:py-4 md:px-12 lg:px-20">
          <EmptyState message={t('empty.noModulesRegistered')} />
        </div>
      );
    }

    const { earlyIndex, lateIndex } = schedulerPositions;
    const { midtermIndex, finalIndex, midtermReport, finalReport } = reportPositions;

    return (
      <div className="px-4 py-3 sm:px-6 sm:py-4 md:px-12 lg:px-20">
        {/* Dev Mode Preview - shows all module card status variations */}
        {DEV_MODE && <DevModePreview />}

        {modules.map((module, index) => (
          <div key={module.id}>
            {/* Grades - positioned independently by published_at */}
            {gradePositions.filter(g => g.insertIndex === index).map(grade => (
              <GradeCard key={grade.category} examScores={grade} reportType={grade.category} />
            ))}

            {/* Final Report - insert BEFORE this module if index matches */}
            {finalReport && index === finalIndex && (
              <ReportCard report={finalReport} />
            )}

            {/* Midterm Report - insert BEFORE this module if index matches */}
            {midtermReport && index === midtermIndex && midtermIndex !== finalIndex && (
              <ReportCard report={midtermReport} />
            )}

            {/* Both reports at same position - show midterm first */}
            {midtermReport && finalReport && midtermIndex === finalIndex && index === midtermIndex && (
              <ReportCard report={midtermReport} />
            )}

            <ModuleCard
              module={module}
              isToday={isToday(module.date)}
              ref={index === todayIndex ? todayRef : null}
            />

            {/* Late scheduler - after 2nd-to-last module (index 1), 3rd position from top */}
            {index === lateIndex && (
              <AppointmentScheduler
                type="late"
                studentId={selectedChild?.id}
                studentName={`${selectedChild?.first_name || ''} ${selectedChild?.last_name || ''}`.trim()}
                courseId={selectedCourse?.id}
                courseName={selectedCourse?.course_title || ''}
              />
            )}

            {/* Early scheduler - before first non-canceled class */}
            {index === earlyIndex && earlyIndex !== lateIndex && (
              <AppointmentScheduler
                type="early"
                studentId={selectedChild?.id}
                studentName={`${selectedChild?.first_name || ''} ${selectedChild?.last_name || ''}`.trim()}
                courseId={selectedCourse?.id}
                courseName={selectedCourse?.course_title || ''}
              />
            )}
          </div>
        ))}

        {/* Grades that should appear after all modules */}
        {gradePositions.filter(g => g.insertIndex === modules.length).map(grade => (
          <GradeCard key={grade.category} examScores={grade} reportType={grade.category} />
        ))}

        {/* Reports that should appear after all modules (at the very end/bottom) */}
        {finalReport && finalIndex === modules.length && (
          <ReportCard report={finalReport} />
        )}
        {midtermReport && midtermIndex === modules.length && midtermIndex !== finalIndex && (
          <ReportCard report={midtermReport} />
        )}
      </div>
    );
  };

  // Transition key: changes on course change AND loading state change
  // This gives exactly 2 fades: old → skeleton → new
  const isLoading = coursesLoading || combinedLoading ||
    (!selectedCourse && courses.length > 0) ||
    (selectedCourse?.is_competition_research && selectedCourse?.needs_parent_input && competitionStatusLoading);

  const transitionKey = `${selectedChild?.id}-${selectedCourse?.id || 'none'}-${isLoading ? 'loading' : 'ready'}`;

  return (
    <FadeTransition transitionKey={transitionKey}>
      {renderContent()}
    </FadeTransition>
  );
}
